interface TileStyleParams {
	tile?: {
		x: number;
		y: number;
		shape: {
			label: string;
			size: string;
			width?: string;
			height?: string;
			modifier?: string;
			rotate?: number;
		};
		customRotation?: number;
		pattern?: {
			name: string;
			label: string;
			grid_properties?: {
				pattern: string;
			};
		};
		color?: {
			image?: string;
		};
	};
	pattern?: string;
	preview?: boolean;
	copyPreview?: boolean;
	shapePicker?: boolean;
	placementRotation?: number;
	placementRotationOffset?: number;
}

type CSSValue = string | number;

interface TileStyle {
	[key: string]: CSSValue | undefined;
	position: 'absolute';
	left: string;
	top: string;
	width?: string;
	height?: string;
	transform?: string;
	transformOrigin?: string;
	clipPath?: string;
}

interface SnapSpacing {
	x: number;
	y: number;
}

// Helper function to get element dimensions based on shape
const elementDimensions = (data: TileStyleParams): TileStyle => {
	if (!data.tile) return { position: 'absolute', left: '0', top: '0' };

	const shape = `${data.tile.shape.label}${data.tile.shape.modifier ? '-' + data.tile.shape.modifier : ''}`;
	const style: TileStyle = {
		position: 'absolute',
		left: '0',
		top: '0'
	};

	// Handle dimensions for each shape type
	if (shape === 'Hex') {
		style.width = '97.92px';
		style.height = '86px';
	} else if (shape === 'Diamond' || shape.startsWith('Diamond-')) {
		style.width = '50px';
		style.height = '86px';
	} else if (shape === 'LargeDiamond' || shape.startsWith('LargeDiamond-')) {
		style.width = '100px';
		style.height = '172px';
	} else if (shape.startsWith('Triangle')) {
		style.width = '100px';
		style.height = '100px';
	} else if (shape === 'LargeTriangle' || shape === 'LargeTriangle-Vertical') {
		style.width = '101px';
		style.height = '88px';
	} else if (shape === 'SmallTriangle' || shape === 'SmallTriangle-Vertical') {
		style.width = '51px';
		style.height = '44px';
	} else if (shape === 'QuarterCircle' || shape.startsWith('QuarterCircle-')) {
		style.width = '100px';
		style.height = '100px';
	} else if (shape === 'Parallel' || shape === 'ParallelVertical' ||
		shape === 'Cross' || shape === 'BackSlash' ||
		shape === 'ForwardSlash' || shape === '4Square' ||
		// Also check the label directly since some tiles might not have modifiers
		data.tile.shape.label === 'Parallel' ||
		data.tile.shape.label === 'Cross' ||
		data.tile.shape.label === 'BackSlash' ||
		data.tile.shape.label === 'ForwardSlash' ||
		data.tile.shape.label === '4Square') {
		style.width = '100px';
		style.height = '100px';
	} else if (shape === 'MiniShiplap' || shape === 'MiniShiplap-Vertical') {
		style.width = data.tile.shape.width || '25px';
		style.height = data.tile.shape.height || '100px';
	} else if (shape === 'HalfCircle' || shape.startsWith('HalfCircle-')) {
		style.width = '100px';
		style.height = '50px';
	} else if (shape === 'LargeHalfCircle' || shape.startsWith('LargeHalfCircle-')) {
		style.width = '200px';
		style.height = '100px';
	} else if (shape === 'Shiplap') {
		style.width = '200px';
		style.height = '50px';
	} else if (shape === 'Shiplap-Vertical') {
		style.width = '50px';
		style.height = '200px';
	} else if (shape === 'Half') {
		style.width = '100px';
		style.height = '50px';
	} else if (shape === 'Half-Vertical') {
		style.width = '50px';
		style.height = '100px';
	} else if (shape === 'Quarter') {
		style.width = '50px';
		style.height = '50px';
	} else if (shape === 'Pixel') {
		style.width = '25px';
		style.height = '25px';
	} else if (shape === 'Bit') {
		style.width = '17.5px';
		style.height = '17.5px';
	} else if (shape === 'LargeSquare') {
		style.width = '200px';
		style.height = '200px';
	} else if (shape === 'Offset') {
		style.width = '200px';
		style.height = '200px';
	} else if (shape === 'LargeHalflap') {
		style.width = '200px';
		style.height = '100px';
	} else if (shape === 'LargeHalflap-Vertical') {
		style.width = '100px';
		style.height = '200px';
	} else if (shape === 'Tangent' || shape === 'CrissCross' || shape === 'Knot' || shape === 'Converge' || data.pattern === 'Square') {
		style.width = '200px';
		style.height = '200px';
	} else if (shape === 'Topo') {
		style.width = '200px';
		style.height = '200px';
	} else if (shape === 'Arc') {
		style.width = '200px';
		style.height = '200px';
	} else if (shape === 'LargeHalfCircle-Vertical') {
		style.width = '100px';
		style.height = '200px';
	} else if (shape === 'LargeHalfCircle-VerticalFlipped') {
		style.width = '100px';
		style.height = '200px';
	} else if (shape === 'LargeHalfCircle-HorizontalFlipped') {
		style.width = '200px';
		style.height = '100px';
	} else if (shape === 'HalfCircle-Vertical') {
		style.width = '50px';
		style.height = '100px';
	} else if (shape === 'HalfCircle-VerticalFlipped') {
		style.width = '50px';
		style.height = '100px';
	} else if (shape === 'HalfCircle-HorizontalFlipped') {
		style.width = '100px';
		style.height = '50px';
	} else if (shape === 'QuarterCircle-BottomLeft') {
		style.width = '100px';
		style.height = '100px';
	} else if (shape === 'QuarterCircle-BottomRight') {
		style.width = '100px';
		style.height = '100px';
	} else if (shape === 'QuarterCircle-TopLeft') {
		style.width = '100px';
		style.height = '100px';
	} else if (shape === 'QuarterCircle-TopRight') {
		style.width = '100px';
		style.height = '100px';
	} else {
		// Default case - use shape size
		const [width, height] = data.tile.shape.size.split('x');
		style.width = `${width}px`;
		style.height = `${height}px`;
	}

	return style;
};

// Helper function to calculate snap spacing
const snapSpacing = (shape: string, pattern?: string): SnapSpacing => {
	const spacing = { x: 0, y: 0 };

	if (pattern === '45 Degree') {
		if (shape === 'Pixel') {
			spacing.x = 2.55;
			spacing.y = 2.55;
		} else if (shape === 'Quarter') {
			spacing.x = 7.75;
			spacing.y = 7.75;
		} else if (shape === 'Half') {
			spacing.y = 7.5;
		} else if (shape === 'Half-Vertical') {
			spacing.x = 7.5;
		} else if (shape === 'Shiplap') {
			spacing.x = 2.8;
			spacing.y = 7.1;
		} else if (shape === 'Shiplap-Vertical') {
			spacing.x = 7.1;
			spacing.y = 2.8;
		} else if (shape === 'LargeSquare') {
			spacing.x = 2.2;
			spacing.y = 2.2;
		} else if (shape === 'MiniShiplap') {
			spacing.x = -1;
			spacing.y = -4.2;
		} else if (shape === 'MiniShiplap-Vertical') {
			spacing.x = -5.5;
			spacing.y = 0;
		} else if (shape === 'LargeHalflap') {
			spacing.x = 1.8;
			spacing.y = 0;
		} else if (shape === 'LargeHalflap-Vertical') {
			spacing.x = -0.5;
			spacing.y = 1;
		} else if (shape === 'QuarterCircle-BottomLeft') {
			spacing.x = 7;
			spacing.y = 7;
		} else if (shape === 'QuarterCircle-BottomRight') {
			spacing.x = 7;
			spacing.y = 7;
		} else if (shape === 'HalfCircle-Vertical') {
			spacing.y = 7;
		} else if (shape === 'HalfCircle-VerticalFlipped') {
			spacing.y = -9;
		} else if (shape === 'HalfCircle-HorizontalFlipped') {
			spacing.y = -9;
			spacing.x = -1;
		}
	}

	if (pattern === '90 Degree Hex') {
		if (shape === 'LargeDiamond') {
			spacing.y = -31;
			spacing.x = -4;
		} else if (shape === 'LargeDiamond-TopLeft') {
			spacing.y = -31;
			spacing.x = -4;
		} else if (shape === 'LargeDiamond-TopRight') {
			spacing.y = -31;
			spacing.x = -4;
		}
	}

	return spacing;
};

export const tileStyle = (data: TileStyleParams): TileStyle => {
	// Handle preview case
	if (data.preview) {
		data.tile = {
			x: 0,
			y: 0,
			shape: data.tile?.shape || { label: '', size: '0x0' }
		};
	}

	if (!data.tile) return { position: 'absolute', left: '0', top: '0' };

	const tile = data.tile;
	const pattern = data.pattern;
	console.log("pattern", pattern)

	// Get base dimensions from elementDimensions
	const baseStyle = elementDimensions(data);
	const style: TileStyle = {
		...baseStyle,
		position: 'absolute',
		left: `${tile.x}px`,
		top: `${tile.y}px`,
		transformOrigin: 'center center'
	};

	// Set dimensions and clip paths based on shape
	const shape = `${tile.shape.label}${tile.shape.modifier ? '-' + tile.shape.modifier : ''}`;

	if (!data.shapePicker) {
		// Handle MiniShiplap tiles
		if (shape === 'MiniShiplap' || shape === 'MiniShiplap-Vertical') {
			style.width = tile.shape.width || '25px';
			style.height = tile.shape.height || '100px';
		}
		// Handle other shapes...
		else if (tile.shape.label === 'Tile') {
			style.width = tile.shape.width || '100px';
			style.height = tile.shape.height || '100px';
		} else if (shape === 'LargeSquare') {
			style.width = '200px';
			style.height = '200px';
		} else {
			switch (shape) {
				case 'Hex':
					style.clipPath = 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)';
					if (pattern === '90 Degree Hex') {
						style.transform = 'rotate(90deg)';
					}
					break;

				case 'Diamond':
					style.clipPath = 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)';
					if (pattern === '90 Degree Hex') {
						style.transform = 'rotate(90deg)';
					}
					break;

				case 'Diamond-TopLeft':
					style.clipPath = 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)';
					style.transform = pattern === '90 Degree Hex' ? 'rotate(150deg)' : 'rotate(60deg)';
					break;

				case 'Diamond-TopRight':
					style.clipPath = 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)';
					style.transform = pattern === '90 Degree Hex' ? 'rotate(210deg)' : 'rotate(120deg)';
					break;

				case 'LargeDiamond':
					style.clipPath = 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)';
					if (pattern === '90 Degree Hex') {
						style.transform = 'rotate(90deg)';
					}
					break;

				case 'LargeDiamond-TopLeft':
					style.clipPath = 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)';
					style.transform = pattern === '90 Degree Hex' ? 'rotate(150deg)' : 'rotate(60deg)';
					break;

				case 'LargeDiamond-TopRight':
					style.clipPath = 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)';
					style.transform = pattern === '90 Degree Hex' ? 'rotate(210deg)' : 'rotate(120deg)';
					break;

				case 'Triangle':
					style.clipPath = 'polygon(0 0, 0% 100%, 100% 100%)';
					break;

				case 'Triangle-TopLeft':
					style.clipPath = 'polygon(100% 0, 0 0, 0 100%)';
					break;

				case 'Triangle-TopRight':
					style.clipPath = 'polygon(100% 100%, 100% 0, 0 0)';
					break;

				case 'Triangle-BottomRight':
					style.clipPath = 'polygon(0 100%, 100% 100%, 100% 0)';
					break;

				case 'LargeTriangle':
				case 'LargeTriangle-Vertical':
					style.clipPath = 'polygon(50% 0%, 0% 100%, 100% 100%)';
					if (pattern === '90 Degree Hex' && shape === 'LargeTriangle') {
						style.transform = 'rotate(90deg)';
					}
					break;

				case 'SmallTriangle':
				case 'SmallTriangle-Vertical':
					style.clipPath = 'polygon(50% 0%, 0% 100%, 100% 100%)';
					if (pattern === '90 Degree Hex') {
						style.transform = 'rotate(90deg)';
					}
					break;

				default:
					// No changes needed for default case
					break;
			}
		}
	}

	// Handle preview and copyPreview offsets
	if (data.preview || data.copyPreview) {
		let offsetX = -25;
		let offsetY = -25;

		if (pattern === 'Square' || pattern === '45 Degree' || pattern === 'Hex' ||
			pattern === 'Freeform' || pattern === '90 Degree Hex' ||
			pattern === 'Spaced Hex' || pattern === 'Argyle') {

			if (shape === 'MiniShiplap') {
				offsetX = -25;
				offsetY = 0;
			} else if (shape === 'MiniShiplap-Vertical') {
				offsetX = 0;
				offsetY = -15;
			} else if (shape === 'SmallTriangle') {
				offsetX = -20;
				offsetY = -20;
			} else if (shape === 'SmallTriangle-Vertical') {
				offsetX = -5;
				offsetY = -15;
			} else if (shape === 'LargeTriangle') {
				offsetX = -40;
				offsetY = -30;
			} else if (shape === 'LargeTriangle-Vertical') {
				offsetX = -30;
				offsetY = -40;
			} else if (shape === 'Pixel') {
				offsetX = 0;
				offsetY = 0;
			} else if (shape === 'Bit') {
				offsetX = -3;
				offsetY = -3;
			} else if (shape === 'Quarter') {
				offsetX = -12;
				offsetY = -12;
			} else if (shape === 'Triangle') {
				offsetX = 0;
				offsetY = -50;
			} else if (shape === 'LargeSquare') {
				offsetX = -75;
				offsetY = -75;
			} else if (shape === 'LargeHalfCircle-Vertical' ||
				shape === 'LargeHalfCircle-Horizontal' ||
				shape === 'LargeHalfCircle-VerticalFlipped' ||
				shape === 'LargeHalfCircle-HorizontalFlipped') {
				offsetX = -100;
				offsetY = -50;
			} else if (shape === 'Triangle-TopRight') {
				offsetX = -50;
			} else if (shape === 'Triangle-BottomRight') {
				offsetX = -50;
				offsetY = -50;
			}

			// Apply spacing from snapSpacing
			const spacing = snapSpacing(shape, pattern);
			style.left = `${parseFloat(style.left as string) + offsetX + spacing.x}px`;
			style.top = `${parseFloat(style.top as string) + offsetY + spacing.y}px`;
		}
	}

	// Handle pattern-specific transformations
	if (pattern === '45 Degree' || pattern === 'Argyle') {
		style.transform = 'rotate(45deg)';
	}

	// Handle custom rotation
	if (tile.customRotation) {
		style.transform = `rotate(${tile.customRotation}deg)`;
	}

	// Handle preview rotation
	if (data.preview && data.placementRotation) {
		if (pattern === '45 Degree') {
			if (shape && shape.includes('LargeHalfCircle')) {
				data.placementRotationOffset = 45;
			}
			if (shape && shape.includes('QuarterCircle')) {
				data.placementRotationOffset = 45;
			}
			if (shape && shape.includes('HalfCircle')) {
				data.placementRotationOffset = 45;
			}
			if (shape && shape.includes('Converge')) {
				data.placementRotationOffset = 45;
			}
			if (shape && shape.includes('Tangent')) {
				data.placementRotationOffset = 45;
			}
			if (shape && shape.includes('CrissCross')) {
				data.placementRotationOffset = 45;
			}
			if (shape && shape.includes('Topo')) {
				data.placementRotationOffset = 45;
			}
			if (shape && shape.includes('Offset')) {
				data.placementRotationOffset = 45;
			}
			if (shape && shape.includes('Arc')) {
				data.placementRotationOffset = 45;
			}
		}
		style.transform = `rotate(${data.placementRotation + (data.placementRotationOffset || 0)}deg)`;
	}

	// Handle rotation for engraved tiles
	if (shape === 'Tangent' || shape === 'CrissCross' || shape === 'Knot' ||
		shape === 'Converge' || shape === 'Topo' || shape === 'Arc') {
		if (tile.customRotation) {
			style.transform = `rotate(${tile.customRotation}deg)`;
		} else if (pattern === '45 Degree') {
			style.transform = 'rotate(45deg)';
		}
	}

	return style;
};

/**
 * Convert inches to pixels based on DPI (dots per inch)
 * @param inches - The measurement in inches
 * @param dpi - Dots per inch (default: 72, which is standard for PDF)
 * @returns The equivalent measurement in pixels
 */
export const inchesToPixels = (inches: number, dpi: number = 72): number => {
	return inches * dpi;
}

/**
 * Convert pixels to inches based on DPI
 * @param pixels - The measurement in pixels
 * @param dpi - Dots per inch (default: 72, which is standard for PDF)
 * @returns The equivalent measurement in inches
 */
export const pixelsToInches = (pixels: number, dpi: number = 72): number => {
	return pixels / dpi;
}

/**
 * Common DPI values for reference
 */
export const DPI = {
	PDF_STANDARD: 72,      // Standard PDF resolution
	SCREEN_STANDARD: 96,   // Standard screen resolution
	PRINT_STANDARD: 300,   // Standard print resolution
	PRINT_HIGH: 600        // High quality print resolution
} as const; 